import { useCallback, useEffect, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import isElement from 'lodash/isElement';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { rootSiteSelector, siteFromRootSiteSelector } from 'selectors/root-site';
import { EXTEND_COLORS } from 'constants/colors';
import { hexToRgb } from 'utils/common';

export const useColorPalette = (colorPalette, target) => {
  const element = useMemo(() => (isElement(target) ? target : document.documentElement), [target]);

  const clearColorPalette = useCallback(() => {
    element.style.removeProperty('--primary-color');
    element.style.removeProperty('--accent-color');
    element.style.removeProperty('--accent-pro');
    element.style.removeProperty('--accent-pro-light');
    element.style.removeProperty('--gray-color');
    element.style.removeProperty('--light-gray');

    element.style.removeProperty('--primary-color-rgb');
    element.style.removeProperty('--accent-color-rgb');
    element.style.removeProperty('--accent-pro-rgb');
    element.style.removeProperty('--accent-pro-light-rgb');
    element.style.removeProperty('--gray-color-rgb');
    element.style.removeProperty('--light-gray-rgb');
  }, [element]);

  useEffect(() => {
    if (!isEmpty(colorPalette)) {
      if (!isEmpty(colorPalette.darkBlue)) {
        element.style.setProperty('--primary-color', colorPalette.darkBlue);
        element.style.setProperty('--primary-color-rgb', hexToRgb(colorPalette.darkBlue));
      }
      if (!isEmpty(colorPalette.orange)) {
        element.style.setProperty('--accent-color', colorPalette.orange);
        element.style.setProperty('--accent-color-rgb', hexToRgb(colorPalette.orange));
      }
      if (!isEmpty(colorPalette.darkerGreen)) {
        element.style.setProperty('--accent-pro', colorPalette.darkerGreen);
        element.style.setProperty('--accent-pro-rgb', hexToRgb(colorPalette.darkerGreen));
      }
      if (!isEmpty(colorPalette.lightGreen)) {
        element.style.setProperty('--accent-pro-light', colorPalette.lightGreen);
        element.style.setProperty('--accent-pro-light-rgb', hexToRgb(colorPalette.lightGreen));
      }
      if (!isEmpty(colorPalette.gray)) {
        element.style.setProperty('--gray-color', colorPalette.gray);
        element.style.setProperty('--gray-color-rgb', hexToRgb(colorPalette.gray));
      }
      if (!isEmpty(colorPalette.lightGray)) {
        element.style.setProperty('--light-gray', colorPalette.lightGray);
        element.style.setProperty('--light-gray-rgb', hexToRgb(colorPalette.lightGray));
      }
    } else {
      clearColorPalette();
    }

    return () => {
      clearColorPalette();
    };
  }, [clearColorPalette, colorPalette, element]);
};

export const useApplySiteCustomColors = (customColors, colorVariables) => {
  const { siteSlug } = useParams();
  const { colorPalette } = useSelector(siteSlug ? siteFromRootSiteSelector(siteSlug) : rootSiteSelector);

  return useMemo(() => {
    let colorsStyle = {};

    forEach(customColors, (value, attribute) => {
      const variable = colorVariables[attribute];
      const color = get(colorPalette, value) || EXTEND_COLORS[value];

      if (variable && color) {
        colorsStyle[variable] = color;
      }
    });

    return colorsStyle;
  }, [colorPalette, colorVariables, customColors]);
};
